import { useState, useEffect, useRef } from 'react';

import {
  StyledSelect,
  StyledSelectOutput,
  StyledOptionsWrapper,
  StyledOptionsList,
  StyledOption,
  StyledIconDown,
  StyledScrollBar,
  StyledScrollBarRunner,
} from './StyledSelect';

export const Select = ({
  options,
  open = false,
  active = false,
  selected = null,
  disabled = false,
  className = '',
  fn,
  closeOnSelect = true,
  multy = false,
  search = false,
}) => {
  const [isOpenedSelect, setIsOpenedSelect] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    selected || multy ? [0] : 0,
  );
  const [dragStarted, setDragStarted] = useState(false);

  const [filteredOptions, setFilteredOptions] = useState(options);
  const [value, setValue] = useState('');

  const inputRef = useRef(null);
  const wheelRef = useRef(null);
  const runnerRef = useRef(null);

  const scrollRef = useRef(null);
  const scrollRefOverflow = useRef(null);
  const currentShiftRef = useRef(null);

  const toggleOpen = e => {
    if (search) {
      setValue('');
      setFilteredOptions(options);
    }
    if (dragStarted) {
      e.stopPropagation();
      return setDragStarted(false);
    }
    if (active) {
      e.stopPropagation();
      setIsOpenedSelect(!isOpenedSelect);
    }
  };

  const scrollList = e => {
    e.preventDefault();
    const parent = wheelRef.current;
    const child = parent.children[0];
    const parentHeight = parent.offsetHeight;
    const childHeight = child.offsetHeight;

    const direction = Math.sign(e.deltaY);

    const currentShift =
      child
        .getAttribute('style')
        ?.match(/translateY\((?<shift>-*\d+\.*\d*)px\)/)?.groups.shift ?? 0;
    let shift = currentShift - direction * 15;
    if (parentHeight - childHeight > shift) shift = parentHeight - childHeight;
    if (shift > 0) shift = 0;
    child.style.transform = `translateY(${shift}px)`;

    const runner = runnerRef.current;
    runner.style.transform = `translateY(${
      -(shift * parentHeight) / childHeight
    }px)`;
  };

  const swipeList = e => {
    const parent = wheelRef.current;
    const child = parent.children[0];
    const parentHeight = parent.offsetHeight;
    const childHeight = child.offsetHeight;

    if (!e.detail.started) {
      currentShiftRef.current =
        child
          .getAttribute('style')
          ?.match(/translateY\((?<shift>-*\d+\.*\d*)px\)/)?.groups.shift ?? 0;

      if (!scrollRef.current) {
        let check = e.target.parentElement;
        while (check) {
          if (check.scrollTop !== 0 || check === document.body) {
            scrollRef.current = check;
            check = null;
          } else check = check.parentElement;
        }
      }
      if (scrollRef.current) {
        scrollRefOverflow.current = scrollRef.current.style.overflow;
        scrollRef.current.style.overflow = 'hidden';
      }
    }
    if (!e.detail.ended) {
      const direction = e.detail.dirY;
      const shift = currentShiftRef.current - -direction;

      if (childHeight - parentHeight <= -shift || shift >= 0) return;

      child.style.transform = `translateY(${shift}px)`;

      const runner = runnerRef.current;
      runner.style.transform = `translateY(${
        -(shift * parentHeight) / childHeight
      }px)`;
    } else if (e.detail.started) {
      if (scrollRef.current) {
        scrollRef.current.style.overflow = scrollRefOverflow.current;
      }
      scrollRef.current = null;
      scrollRefOverflow.current = null;
    }
  };

  const startDrag = e => {
    if (e.button !== 0 || !e.buttons) return;
    setDragStarted(e.clientY);
  };

  const stopDrag = e => {
    setDragStarted(false);
  };

  const dragList = e => {
    if (dragStarted) {
      const direction = dragStarted - e.clientY;
      setDragStarted(e.clientY);

      const child = wheelRef.current.children[0];
      const runner = runnerRef.current;
      const rails = runnerRef.current.parentElement;

      const railsHeight = rails.offsetHeight;
      const runnerHeight = runner.offsetHeight;

      const runnerShift =
        runner
          .getAttribute('style')
          ?.match(/translateY\((?<shift>-*\d+\.*\d*)px\)/)?.groups.shift ?? 0;

      let newRunnerShift = runnerShift - direction;

      if (railsHeight - runnerHeight < newRunnerShift || newRunnerShift < 0)
        return;

      child.style.transform = `translateY(${
        (-newRunnerShift * child.offsetHeight) /
        child.parentElement.offsetHeight
      }px)`;

      runner.style.transform = `translateY(${newRunnerShift}px)`;
    }
  };

  useEffect(() => {
    const wheelEl = wheelRef.current;
    wheelRef.current.addEventListener('wheel', scrollList);
    wheelRef.current.addEventListener('swipe', swipeList);
    return () => {
      wheelEl.removeEventListener('wheel', scrollList);
      wheelEl.removeEventListener('swipe', swipeList);
    };
  }, []);

  useEffect(() => {
    setSelectedOption(selected);
  }, [selected]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const parent = wheelRef.current;

    if ((active && isOpenedSelect) || (!active && open)) {
      const child = parent.children[0];
      const childHeight = child.offsetHeight;
      parent.style.height = `${childHeight}px`;
      parent.style.maxHeight = '155px';
      child.style.transform = 'translateY(0px)';
      setTimeout(() => {
        const parentHeight = wheelRef.current.offsetHeight;
        runnerRef.current.style.height = `${
          (parentHeight / childHeight) * parentHeight - 20
        }px`;
        runnerRef.current.style.transform = 'translateY(0px)';
      }, 200);
    } else parent.style.height = '0px';
  }, [isOpenedSelect, open, active, filteredOptions]);

  const handleSelect = key => e => {
    !closeOnSelect && e.stopPropagation();

    if (multy) {
      if (active) {
        if (key === '0') setSelectedOption([0]);
        else {
          const newOptions = selectedOption.some(el => el === key)
            ? [...selectedOption.filter(el => el !== key)]
            : [...selectedOption, key];
          if (newOptions.length > 1 && newOptions[0] === 0)
            newOptions.unshift();
          if (!newOptions.length) newOptions[0] = 0;
          setSelectedOption(newOptions);
        }
      }

      const selectedValue = isNaN(+key) ? key : +key;

      if (fn) {
        if (selectedValue === 0) fn([0]);
        else {
          const newOptions = selectedOption.some(el => el === selectedValue)
            ? [...selectedOption.filter(el => el !== selectedValue)]
            : [...selectedOption, selectedValue];
          if (newOptions.length > 1 && newOptions[0] === 0) {
            newOptions.shift();
          }
          if (!newOptions.length) newOptions[0] = 0;
          fn(newOptions);
        }
      }
    } else {
      active && setSelectedOption(key);
      const selectedValue = isNaN(+key) ? key : +key;
      fn && fn(selectedValue);
    }
  };

  const handleInput = e => {
    setValue(e.target.value);
    if (!e.target.value) setFilteredOptions(options);
    else {
      const newOptions = {};
      for (let key in options) {
        if (
          String(options[key])
            .toLowerCase()
            .includes(e.target.value.toLowerCase())
        )
          newOptions[key] = options[key];
      }
      if (!newOptions[0]) newOptions[0] = 'Visi variantai';
      setFilteredOptions(newOptions);
    }
  };

  return (
    <StyledSelect
      onClick={toggleOpen}
      onMouseEnter={startDrag}
      onMouseMove={dragList}
      onMouseLeave={stopDrag}
      className={`${open || isOpenedSelect ? 'opened' : ''} ${
        disabled ? 'disabled' : ''
      } ${className}`}
    >
      <StyledIconDown className={open || isOpenedSelect ? 'active' : ''} />
      <StyledSelectOutput
        ref={inputRef}
        value={
          multy
            ? selectedOption[0] === 0
              ? 'Visi variantai'
              : `${selectedOption.length} varianta${
                  selectedOption.length > 1 ? 'i' : 's'
                }`
            : options[selectedOption] || 'Visi variantai'
        }
        readOnly
        className={selected ? 'correct' : ''}
        disabled={disabled}
      />
      {search && open && (
        <StyledSelectOutput
          value={value}
          onClick={e => e.stopPropagation()}
          onInput={handleInput}
          placeholder="Start query"
          autoFocus
          className={'correct search'}
          disabled={disabled}
        />
      )}
      <StyledOptionsWrapper
        ref={wheelRef}
        className={`${open || isOpenedSelect ? 'active' : ''} ${
          disabled ? 'disabled' : ''
        }`}
      >
        <StyledOptionsList style={{ transform: 'translateY(0)' }}>
          {Object.entries(filteredOptions).map(([key, value], i) => (
            <StyledOption
              key={i}
              onClick={handleSelect(key)}
              className={
                (
                  multy
                    ? selectedOption.some(el => String(el) === key)
                    : String(selectedOption) === key
                )
                  ? 'selected'
                  : ''
              }
            >
              {value}
            </StyledOption>
          ))}
        </StyledOptionsList>
        <StyledScrollBar onMouseDown={startDrag}>
          <StyledScrollBarRunner ref={runnerRef} />
        </StyledScrollBar>
      </StyledOptionsWrapper>
    </StyledSelect>
  );
};
